/**
 * Declarative config of the fleet list (`FleetListCard`, fiches 22756 /
 * 22987): the stable columns and the DataList config builder (inline rows,
 * server-side search `query` built with the shared `buildListToolbar`, row
 * click → vehicle detail).
 */

import type { ColumnConfig, DataListConfig } from '@components/integrated/data-list';

import type { FleetVehicle } from '../../api/fleetService';
import { EMPTY_STATE_TEXT } from '../shared/cardText';
import {
  EXTRANET_DATE_RENDERER,
  EXTRANET_ROW_OPEN_HANDLER,
  registerStatusBadgeRenderer,
  type ExtranetRowContext,
} from '../shared/cellRenderers';
import { buildListToolbar, type ListToolbarSpec } from '../shared/listToolbarConfig';
import { FLEET_COMPONENT_IDS } from './fleetComponentIds';
import { FLEET_LIST_TEXT } from './fleetText';
import { VEHICLE_STATUS_BADGE } from './vehicleStatus';

/** Cell renderer of the `statut` column (VEH_SITUATION badge). */
export const VEHICLE_STATUS_RENDERER = registerStatusBadgeRenderer(
  'extranet-vehicleStatus',
  VEHICLE_STATUS_BADGE,
);

/** Stable column keys. */
export type FleetListColumnKey =
  | 'immatriculation'
  | 'marqueModele'
  | 'dateEntree'
  | 'dateSortie'
  | 'statut';

/** Base columns (design order). Dates are pre-formatted strings: not sortable. */
export const FLEET_LIST_COLUMNS: Readonly<Record<FleetListColumnKey, ColumnConfig>> = {
  immatriculation: {
    label: 'Immat.',
    headerTooltip: 'Immatriculation',
    order: 1,
    type: 'text',
    className: 'font-mono font-medium',
    sortable: true,
  },
  marqueModele: {
    label: 'Marque · Modèle',
    order: 2,
    type: 'text',
    sortable: true,
  },
  dateEntree: {
    label: "Date d'entrée",
    order: 3,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: false,
  },
  dateSortie: {
    label: 'Date de sortie',
    order: 4,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: false,
  },
  statut: {
    label: 'Statut',
    order: 5,
    type: 'custom',
    typeOptions: { renderer: VEHICLE_STATUS_RENDERER },
    sortable: true,
  },
};

/**
 * The toolbar of the list (fiche 22987): the search box only (no status
 * select). The page maps the reported search to `query`
 * (`buildFleetListParams`).
 */
export const FLEET_LIST_TOOLBAR: ListToolbarSpec = {
  search: { placeholder: FLEET_LIST_TEXT.searchPlaceholder },
};

/** A fleet row carrying its per-row context (`__ctx.onOpen`). */
export type FleetListRow = FleetVehicle & { __ctx?: ExtranetRowContext };

/**
 * Base DataList config of the fleet list: inline rows (already searched by
 * VEOS) in API order, one page, client-side sort; rows clickable when at
 * least one opens a vehicle.
 */
export function buildFleetListConfig(rows: readonly FleetListRow[]): DataListConfig {
  const clickable = rows.some((row) => row.__ctx?.onOpen !== undefined);
  return {
    id: FLEET_COMPONENT_IDS.fleetList,
    dataSource: { data: rows as unknown as Record<string, unknown>[] },
    columns: { ...FLEET_LIST_COLUMNS },
    display: { size: 'default', hoverable: clickable, striped: true },
    sorting: { enabled: true },
    toolbar: buildListToolbar(FLEET_LIST_TOOLBAR),
    pagination: { mode: 'scroll', pageSize: 999_999 },
    ...(clickable ? { actions: { rowClick: { handler: EXTRANET_ROW_OPEN_HANDLER } } } : {}),
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
